/**
 * Top-level error boundary — the "never a blank screen" backstop
 * (ARCHITECTURE.md §5). Whatever throws below it, the user gets an honest
 * message and a reload button; local data is untouched.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { t } from "../i18n.ts";
import type { Locale } from "../i18n.ts";

interface Props {
  locale: Locale;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // No remote error sink in P1 — console is the surface.
    console.error("[td] top-level error boundary", error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;
    const { locale } = this.props;
    return (
      <main className="error-screen" role="alert">
        <h1>{t(locale, "app.error_title")}</h1>
        <p>{t(locale, "app.error_body")}</p>
        <button type="button" onClick={() => window.location.reload()}>
          {t(locale, "app.reload")}
        </button>
      </main>
    );
  }
}
